import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getScopeCoverage } from '../../utils/shapeUpApi';

const DRAWER_WIDTH = 420;

const Section = ({ icon, title, color, items, renderItem }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color }}>
          {title}
        </Typography>
        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: 11 }} />
      </Box>
      <List dense disablePadding>
        {items.map((item, i) => renderItem(item, i))}
      </List>
    </Box>
  );
};

/**
 * Right-anchored drawer showing AI-powered analysis of scope coverage against the pitch.
 * Highlights gaps in coverage, rabbit hole risks, and no-go violations.
 * Results are cached and limited to 2 analyses per bet per cycle.
 */
const ScopeCoverageDrawer = ({ betId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!betId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getScopeCoverage(betId, refresh);
      setAnalysis(result);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not analyze scope coverage');
    } finally {
      setLoading(false);
    }
  }, [betId]);

  useEffect(() => {
    if (!open || !betId) {
      setAnalysis(null);
      setError(null);
      return;
    }
    load(false);
  }, [betId, open, load]);

  const handleRefresh = () => {
    load(true);
  };

  const gapCount = analysis?.gaps?.length || 0;
  const rabbitHoleCount = analysis?.rabbitHoleRisks?.length || 0;
  const noGoCount = analysis?.noGoRisks?.length || 0;
  const totalIssues = gapCount + rabbitHoleCount + noGoCount;

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      keepMounted={false}
      ModalProps={{ disableScrollLock: true }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: DRAWER_WIDTH },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Scope Coverage Check
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {analysis?.pitchTitle
                ? `Analyzing: ${analysis.pitchTitle}`
                : 'AI analysis comparing scopes to pitch'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Analyzing coverage...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && analysis && (
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {/* Cache and refresh status */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {analysis.cached && (
                  <Tooltip title="Showing cached results">
                    <Chip
                      icon={<CachedIcon sx={{ fontSize: 14 }} />}
                      label="Cached"
                      size="small"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary">
                  {analysis.usageCount}/{analysis.maxUses} analyses used
                </Typography>
              </Box>
              {analysis.canRefresh ? (
                <Button
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Re-analyze
                </Button>
              ) : (
                <Tooltip title="Analysis limit reached for this cycle">
                  <span>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                      disabled
                    >
                      Re-analyze
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            {analysis.limitReached && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Analysis limit reached. Showing last cached result.
              </Alert>
            )}

            {totalIssues === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Looking good! No coverage concerns detected.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {gapCount > 0 && (
                  <Chip
                    icon={<LightbulbOutlinedIcon />}
                    label={`${gapCount} gap${gapCount !== 1 ? 's' : ''}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {rabbitHoleCount > 0 && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${rabbitHoleCount} rabbit hole risk${rabbitHoleCount !== 1 ? 's' : ''}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                {noGoCount > 0 && (
                  <Chip
                    icon={<ErrorOutlineIcon />}
                    label={`${noGoCount} no-go concern${noGoCount !== 1 ? 's' : ''}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            <Section
              icon={<LightbulbOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />}
              title="Gaps in Coverage"
              color="info.main"
              items={analysis.gaps}
              renderItem={(gap, i) => (
                <ListItem key={i} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'info.main',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={gap.concept}
                    secondary={gap.suggestion}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              )}
            />

            <Section
              icon={<WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />}
              title="Rabbit Hole Risks"
              color="warning.main"
              items={analysis.rabbitHoleRisks}
              renderItem={(risk, i) => (
                <ListItem key={i} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={risk.scopeName}
                    secondary={risk.concern}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              )}
            />

            <Section
              icon={<ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />}
              title="No-Go Concerns"
              color="error.main"
              items={analysis.noGoRisks}
              renderItem={(risk, i) => (
                <ListItem key={i} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={risk.scopeName}
                    secondary={risk.concern}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              )}
            />

            {analysis.summary && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Summary
                </Typography>
                <Typography variant="body2">
                  {analysis.summary}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ScopeCoverageDrawer;
